import { useMemo } from "react"

import { Loader } from "~/ui-rtk/components/ui/common"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { formatNumber } from "~/ui-rtk/utils/format"
import useDate from "~/ui-rtk/hooks/date"

import type { LtvMetricMode } from "../constants"
import {
  cohortCellValue,
  firstOrderValue,
  isCellMature,
  weightedAverageAtOffset,
  type CohortGranularity,
  type CohortRow,
} from "../utils/cohort-matrix"

const dayjs = useDate()

// Flat accent for measurable cells, echoing the reference report; masked
// (immature) cells stay transparent so the maturity staircase reads clearly.
const MATURE_CELL_STYLE = { backgroundColor: "rgba(139, 92, 246, 0.14)" }

export type CohortGridProps = {
  granularity: CohortGranularity
  cohortRows: CohortRow[]
  offsets: number[]
  mode: LtvMetricMode
  todayIso: string
  isLoading?: boolean
}

const currency = (value: number | null) =>
  value === null ? "--" : formatNumber(value, { currency: true, decimals: 0 })

export default function CohortGrid({
  granularity,
  cohortRows,
  offsets,
  mode,
  todayIso,
  isLoading = false,
}: CohortGridProps) {
  const cohortLabel = (cohortStart: string) =>
    granularity === "month"
      ? dayjs(cohortStart).format("MMM YYYY")
      : dayjs(cohortStart).format("MMM D, YYYY")

  const averageRow = useMemo(
    () =>
      offsets.map(offset =>
        weightedAverageAtOffset(
          cohortRows,
          offset,
          mode,
          granularity,
          todayIso,
        ),
      ),
    [cohortRows, offsets, mode, granularity, todayIso],
  )

  const averageFirstOrder = useMemo(() => {
    const totals = cohortRows.reduce(
      (acc, row) => ({
        revenue: acc.revenue + row.firstOrderRevenue,
        customers: acc.customers + row.newCustomers,
      }),
      { revenue: 0, customers: 0 },
    )
    return totals.customers > 0 ? totals.revenue / totals.customers : null
  }, [cohortRows])

  const averageNewCustomers = useMemo(
    () =>
      cohortRows.length > 0
        ? cohortRows.reduce((sum, row) => sum + row.newCustomers, 0) /
          cohortRows.length
        : null,
    [cohortRows],
  )

  if (!isLoading && cohortRows.length === 0) {
    return (
      <div className="bg-system-blue-800 rounded-lg p-6 text-gray-light-2">
        No cohort data for the selected period and filters.
      </div>
    )
  }

  return (
    <div className="relative overflow-x-auto rounded-lg border border-basic-blue">
      {isLoading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-basic-black/50">
          <Loader size={50} />
        </div>
      )}
      <table className="table table-sm">
        <thead>
          <tr className="text-gray-light-2">
            <th className="sticky left-0 bg-basic-black z-10 whitespace-nowrap border-r border-basic-blue">
              First order at
            </th>
            <th className="text-right whitespace-nowrap">New customers</th>
            <th className="text-right whitespace-nowrap">First order</th>
            {offsets.map(offset => (
              <th key={offset} className="text-right min-w-16">
                {offset}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cohortRows.map(row => (
            <tr key={row.cohortStart}>
              <td className="sticky left-0 bg-basic-black z-10 whitespace-nowrap font-medium border-r border-basic-blue">
                {cohortLabel(row.cohortStart)}
              </td>
              <td className="text-right">
                {formatNumber(row.newCustomers, { decimals: 0 })}
              </td>
              <td className="text-right" style={MATURE_CELL_STYLE}>
                {currency(firstOrderValue(row))}
              </td>
              {offsets.map(offset => {
                const mature = isCellMature(
                  row.cohortStart,
                  offset,
                  granularity,
                  todayIso,
                )
                return (
                  <td
                    key={offset}
                    className={cn(
                      "text-right tabular-nums",
                      !mature && "text-gray-light-2",
                    )}
                    style={mature ? MATURE_CELL_STYLE : undefined}
                  >
                    {mature
                      ? currency(cohortCellValue(row, offset, mode))
                      : "--"}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
        {cohortRows.length > 0 && (
          <tfoot>
            <tr className="border-t border-basic-blue font-semibold text-basic-white">
              <td className="sticky left-0 bg-basic-black z-10 border-r border-basic-blue">
                Average
              </td>
              <td className="text-right">
                {averageNewCustomers === null
                  ? "--"
                  : formatNumber(averageNewCustomers, { decimals: 0 })}
              </td>
              <td className="text-right">{currency(averageFirstOrder)}</td>
              {offsets.map((offset, index) => {
                const average = averageRow[index]
                return (
                  <td key={offset} className="text-right tabular-nums">
                    {average ? currency(average.value) : "--"}
                  </td>
                )
              })}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  )
}
